import { ArrowLeft, ArrowRight } from "lucide-react";

const Label = ({ children }) => (
  <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[#71717A] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</label>
);
const Input = ({ testid, ...props }) => <input data-testid={testid} className="brutalist-input" {...props} />;
const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-bold text-[#00A3E0] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</h3>
);

export default function MinorWorksStep2({ formData, updateForm, onNext, onPrev }) {
  const set = (field) => (e) => updateForm({ [field]: e.target.value });

  return (
    <div className="form-container max-w-3xl mx-auto" data-testid="mw-step-2">
      <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Circuit Details & Test Results</h2>

      {/* Part 3: Circuit Details */}
      <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
        <SectionTitle>Part 3: Circuit Details</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="form-field"><Label>DB reference</Label><Input testid="mw-db-ref" value={formData.db_ref} onChange={set("db_ref")} /></div>
          <div className="form-field col-span-2"><Label>DB location & type</Label><Input testid="mw-db-location" value={formData.db_location_type} onChange={set("db_location_type")} placeholder="e.g. Hallway, Split load CU" /></div>
          <div className="form-field"><Label>Circuit number</Label><Input testid="mw-circuit-num" value={formData.circuit_number} onChange={set("circuit_number")} /></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <div className="form-field col-span-2"><Label>Circuit description</Label><Input testid="mw-circuit-desc" value={formData.circuit_description} onChange={set("circuit_description")} placeholder="e.g. Ring final circuit - sockets" /></div>
          <div className="form-field"><Label>Reference method</Label><Input testid="mw-ref-method" value={formData.reference_method} onChange={set("reference_method")} placeholder="e.g. C" /></div>
          <div className="form-field"><Label>Live conductor (mm²)</Label><Input testid="mw-live-size" value={formData.conductor_live_size} onChange={set("conductor_live_size")} /></div>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-3">
          <div className="form-field"><Label>No. of lives</Label><Input testid="mw-live-count" value={formData.conductor_live_count} onChange={set("conductor_live_count")} /></div>
          <div className="form-field"><Label>CPC (mm²)</Label><Input testid="mw-cpc-size" value={formData.conductor_cpc_size} onChange={set("conductor_cpc_size")} /></div>
          <div className="form-field"><Label>OCPD BS(EN)</Label><Input testid="mw-ocpd-bs" value={formData.ocpd_bs} onChange={set("ocpd_bs")} /></div>
          <div className="form-field"><Label>OCPD type</Label><Input testid="mw-ocpd-type" value={formData.ocpd_type} onChange={set("ocpd_type")} placeholder="e.g. B" /></div>
          <div className="form-field"><Label>OCPD rating (A)</Label><Input testid="mw-ocpd-rating" value={formData.ocpd_rating} onChange={set("ocpd_rating")} /></div>
          <div className="form-field"><Label>Breaking (kA)</Label><Input testid="mw-ocpd-break" value={formData.ocpd_breaking} onChange={set("ocpd_breaking")} /></div>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-3">
          <div className="form-field"><Label>Time delay (ms)</Label><Input testid="mw-time-delay" value={formData.time_delay} onChange={set("time_delay")} placeholder="N/A" /></div>
          <div className="form-field"><Label>RCD BS(EN)</Label><Input testid="mw-rcd-bs" value={formData.rcd_bs} onChange={set("rcd_bs")} /></div>
          <div className="form-field"><Label>RCD type</Label><Input testid="mw-rcd-type" value={formData.rcd_type} onChange={set("rcd_type")} /></div>
          <div className="form-field"><Label>RCD rating (A)</Label><Input testid="mw-rcd-rating" value={formData.rcd_rating} onChange={set("rcd_rating")} /></div>
          <div className="form-field"><Label>RCD IΔn (mA)</Label><Input testid="mw-rcd-idn" value={formData.rcd_idn} onChange={set("rcd_idn")} /></div>
          <div className="form-field"><Label>AFDD rating (A)</Label><Input testid="mw-afdd" value={formData.afdd_rating} onChange={set("afdd_rating")} placeholder="N/A" /></div>
        </div>
        <div className="mt-3 form-field">
          <Label>SPD type</Label>
          <Input testid="mw-spd" value={formData.spd_type} onChange={set("spd_type")} placeholder="N/A" />
        </div>
      </div>

      {/* Part 4: Test Results */}
      <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
        <SectionTitle>Part 4: Test Results</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="form-field"><Label>R1+R2 (Ohm)</Label><Input testid="mw-r1r2" value={formData.r1r2} onChange={set("r1r2")} /></div>
          <div className="form-field"><Label>Max measured Zs (Ohm)</Label><Input testid="mw-max-zs" value={formData.max_zs} onChange={set("max_zs")} /></div>
          <div className="form-field"><Label>Ring L/L (Ohm)</Label><Input testid="mw-ring-ll" value={formData.ring_ll} onChange={set("ring_ll")} placeholder="N/A" /></div>
          <div className="form-field"><Label>Ring N/N (Ohm)</Label><Input testid="mw-ring-nn" value={formData.ring_nn} onChange={set("ring_nn")} placeholder="N/A" /></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <div className="form-field"><Label>Ring CPC/CPC (Ohm)</Label><Input testid="mw-ring-cpc" value={formData.ring_cpc} onChange={set("ring_cpc")} placeholder="N/A" /></div>
          <div className="form-field"><Label>IR test voltage (V)</Label><Input testid="mw-ir-voltage" value={formData.ir_test_voltage} onChange={set("ir_test_voltage")} /></div>
          <div className="form-field"><Label>IR Live-Live (MOhm)</Label><Input testid="mw-ir-ll" value={formData.ir_live_live} onChange={set("ir_live_live")} /></div>
          <div className="form-field"><Label>IR Live-Earth (MOhm)</Label><Input testid="mw-ir-le" value={formData.ir_live_earth} onChange={set("ir_live_earth")} /></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <div className="form-field"><Label>RCD disconnect time (ms)</Label><Input testid="mw-rcd-time" value={formData.rcd_disconnect_time} onChange={set("rcd_disconnect_time")} /></div>
          <div className="form-field">
            <Label>RCD test button</Label>
            <select data-testid="mw-rcd-btn" value={formData.rcd_test_btn} onChange={set("rcd_test_btn")} className="brutalist-input">
              <option value="N/A">N/A</option>
              <option value="Pass">Pass</option>
              <option value="Fail">Fail</option>
            </select>
          </div>
          <div className="form-field">
            <Label>Polarity</Label>
            <select data-testid="mw-polarity" value={formData.polarity} onChange={set("polarity")} className="brutalist-input">
              <option value="N/A">N/A</option>
              <option value="Pass">Pass</option>
              <option value="Fail">Fail</option>
            </select>
          </div>
          <div className="form-field">
            <Label>AFDD test button</Label>
            <select data-testid="mw-afdd-btn" value={formData.afdd_test_btn} onChange={set("afdd_test_btn")} className="brutalist-input">
              <option value="N/A">N/A</option>
              <option value="Pass">Pass</option>
              <option value="Fail">Fail</option>
            </select>
          </div>
        </div>
        <div className="mt-3">
          <Label>SPD functionality confirmed</Label>
          <select data-testid="mw-spd-confirm" value={formData.spd_confirmed} onChange={set("spd_confirmed")} className="brutalist-input max-w-[200px]">
            <option value="N/A">N/A</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
      </div>

      <div className="flex justify-between">
        <button data-testid="mw-step-2-prev" onClick={onPrev} className="brutalist-btn-outline flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Previous</button>
        <button data-testid="mw-step-2-next" onClick={onNext} className="brutalist-btn flex items-center gap-2">Next <ArrowRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
